import { useReducer, Dispatch } from 'react'
import { NotificationItem, PaginationMeta } from '../components/types'

/**
 * Filter options for notifications display
 */
export type NotificationFilter = 'all' | 'unread' | 'read' | 'invitations'

/**
 * Notification state interface
 */
export interface NotificationsState {
  /** Current list of notifications */
  list: NotificationItem[]
  /** Current page number for pagination */
  page: number
  /** Pagination metadata from API */
  pagination: PaginationMeta | null
  /** All notifications data from API */
  notifications: NotificationItem[]
  /** Loading state for API requests */
  notificationLoading: boolean
  /** Error message if API request fails */
  notificationError: string | null
  /** ID of notification with open delete confirmation */
  notificationOpenForDelete: string | null
  /** Current active filter */
  filter: NotificationFilter
  /** Count of unread notifications */
  unreadCount: number
  /** Count of unread invitations */
  invitationsUnreadCount: number
}

/**
 * Action types for the notifications reducer
 */
export type NotificationsAction =
  | { type: 'SET_NOTIFICATIONS'; payload: NotificationItem[] }
  | { type: 'APPEND_NOTIFICATIONS'; payload: NotificationItem[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_FILTER'; payload: NotificationFilter }
  | { type: 'SET_PAGINATION'; payload: PaginationMeta | null }
  | { type: 'SET_NOTIFICATION_OPEN_FOR_DELETE'; payload: string | null }
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | {
      type: 'SET_UNREAD_COUNTS'
      payload: { unread: number; invitations_unread: number }
    }
  | { type: 'RESET_STATE' }

/**
 * Initial state for notifications
 */
const initialState: NotificationsState = {
  list: [],
  page: 1,
  pagination: null,
  notifications: [],
  notificationLoading: false,
  notificationError: null,
  notificationOpenForDelete: null,
  filter: 'all',
  unreadCount: 0,
  invitationsUnreadCount: 0,
}

/**
 * Sorts notifications by date and updates list
 */
const sortByDate = (a: NotificationItem, b: NotificationItem): number => {
  const aDate = new Date(a.created_at).getTime()
  const bDate = new Date(b.created_at).getTime()
  return bDate - aDate
}

/**
 * Reducer function for notifications state management
 */
const notificationsReducer = (
  state: NotificationsState,
  action: NotificationsAction
): NotificationsState => {
  console.log('🔄 notificationsReducer: Action dispatched:', action.type)

  switch (action.type) {
    case 'SET_NOTIFICATIONS': {
      const sortedNotifications = [...action.payload].sort(sortByDate)
      return {
        ...state,
        notifications: action.payload,
        list: sortedNotifications,
      }
    }

    case 'APPEND_NOTIFICATIONS': {
      const allNotifications = [...state.notifications, ...action.payload]
      const sortedNotifications = [...allNotifications].sort(sortByDate)
      return {
        ...state,
        notifications: allNotifications,
        list: sortedNotifications,
      }
    }

    case 'SET_LOADING':
      return {
        ...state,
        notificationLoading: action.payload,
      }

    case 'SET_ERROR':
      return {
        ...state,
        notificationError: action.payload,
      }

    case 'SET_PAGE':
      return {
        ...state,
        page: action.payload,
      }

    case 'SET_FILTER':
      return {
        ...state,
        filter: action.payload,
      }

    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload,
      }

    case 'SET_NOTIFICATION_OPEN_FOR_DELETE':
      return {
        ...state,
        notificationOpenForDelete: action.payload,
      }

    case 'SET_UNREAD_COUNT':
      return {
        ...state,
        unreadCount: action.payload,
      }

    case 'SET_UNREAD_COUNTS':
      return {
        ...state,
        unreadCount: action.payload.unread,
        invitationsUnreadCount: action.payload.invitations_unread,
      }

    case 'RESET_STATE':
      return {
        ...initialState,
      }

    default:
      return state
  }
}

/**
 * Custom hook for managing notifications state with useReducer
 * @returns {[NotificationsState, Dispatch<NotificationsAction>]} State and dispatch function
 */
export const useNotificationsReducer = (): [
  NotificationsState,
  Dispatch<NotificationsAction>
] => {
  console.log('🔄 useNotificationsReducer: Initializing reducer')
  return useReducer(notificationsReducer, initialState)
}
